/**
 * 媒體庫的網格檢視。
 *
 * 挑圖的時候檔名幫不上忙——`1_pic1.png` 在 20 個資料夾裡都叫這個名字，要分辨
 * 只能看內容。網格把縮圖放大到 160 px，一眼掃過去就知道是哪張。
 *
 * **只在圖片較多時才划算**：縮圖是逐檔 fetch 成 blob（內容端點要帶
 * Authorization，`<img src>` 帶不了 header），一頁 50 張圖等於 50 個請求。
 * 影片與語音一律不載內容——那等於下載整個檔案，代價完全不成比例，改以圖示
 * 加副檔名呈現。所以預設仍是清單檢視，網格是使用者主動切換的。
 */

import { Button } from './Button';
import { MediaEditFields, MediaMetaLine, type MediaViewProps } from './MediaItemView';
import { MediaThumb } from './MediaPreview';
import { formatBytes } from '../lib/media-upload';

export function MediaGrid(props: MediaViewProps): JSX.Element {
  const {
    items,
    languages,
    selectedIds,
    onToggle,
    editing,
    onEditChange,
    onSave,
    saving,
    onPreview,
    onDelete,
    onShowUsage,
    onFolder,
  } = props;

  if (items.length === 0) {
    return (
      <div className="table-card" style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
        此處尚無素材
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        // auto-fill + minmax：視窗寬度決定每列幾張，不必自己算斷點
        gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
        gap: 12,
      }}
    >
      {items.map((m) => {
        const isEditing = editing?.id === m.id;
        const selected = selectedIds.has(m.id);
        return (
          <div
            key={m.id}
            style={{
              border: `1px solid ${selected ? '#93c5fd' : '#e5e7eb'}`,
              background: selected ? '#f5f8ff' : '#fff',
              borderRadius: 8,
              padding: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              minWidth: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={selected} onChange={() => onToggle(m.id)} />
              <span style={{ fontSize: 11, color: '#6b7280' }}>
                {m.media_type} · {formatBytes(Number(m.size_bytes))}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280' }}>
                {m.language ?? '—'}
              </span>
            </div>

            <button
              onClick={() => onPreview(m)}
              title="預覽"
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                background: '#f9fafb',
                cursor: 'pointer',
                height: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                padding: 0,
              }}
            >
              <MediaThumb item={m} size={160} />
            </button>

            <div
              style={{
                fontSize: 12,
                wordBreak: 'break-all',
                // 檔名最多兩行；再長就截斷，否則卡片高度會參差不齊
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
              title={m.filename}
            >
              {m.filename}
            </div>

            {isEditing && editing ? (
              <>
                <MediaEditFields editing={editing} languages={languages} onChange={onEditChange} />
                <div style={{ display: 'flex', gap: 4 }}>
                  <Button variant="primary" onClick={() => onSave(editing)} disabled={saving}>
                    儲存
                  </Button>
                  <Button variant="secondary" onClick={() => onEditChange(null)}>
                    取消
                  </Button>
                </div>
              </>
            ) : (
              <>
                <MediaMetaLine item={m} onFolder={onFolder} onShowUsage={onShowUsage} />
                <div style={{ display: 'flex', gap: 4, marginTop: 'auto' }}>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      onEditChange({
                        id: m.id,
                        language: m.language ?? '',
                        tags: m.tags.join(', '),
                      })
                    }
                  >
                    編輯
                  </Button>
                  <Button variant="danger" onClick={() => onDelete(m)}>
                    刪除
                  </Button>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
